import * as React from 'react';
import { styled } from '@glitz/react';
import { SiteLogo } from 'webapp/shared/logos';
import { ButtonLink } from 'webapp/components/button';
import { isSvenssons, Theme } from 'webapp/theme';
import { createTransitionDecorator } from '@avensia/excite-ui';
import { ZIndex } from 'webapp/z-index';
import { isComponentType, Link, translate, useCurrentPage, useDynamicData } from '@avensia/excite';
import { CartButton } from 'webapp/shell/header/CartButton';
import { WishListButton } from './WishListButton';
import { WishListButton as NNWishListButton } from 'nordicnest/shell/header/WishListButton';
import { BackIcon } from 'webapp/components/icons';
import { HEADER_HEIGHT } from 'webapp/utils/constants';
import QuickSearch from 'webapp/search/QuickSearch';
import { MenuButton } from 'webapp/shell/header/MenuButton';
import { useShowQuickSearchOnScroll } from 'webapp/shared/use-show-quick-search-on-scroll';
import { MyPagesButton } from './MyPagesButton';

export default function Header() {
  const currentPage = useCurrentPage();
  return isComponentType<NordicNest.Api.CheckoutPageApiModel>(currentPage) ||
    isComponentType<NordicNest.Api.ExpressCheckoutPageApiModel>(currentPage) ? (
    <CheckoutHeader heading={currentPage.page.heading} />
  ) : (
    <CommonHeader />
  );
}

function CommonHeader() {
  const scrollPosition = __BROWSER__ ? window.scrollY : 0;
  const quickSearchRef = React.useRef<{ focusInput: () => void } | null>(null);

  const { showQuickSearch } = useShowQuickSearchOnScroll({
    headerHeight: HEADER_HEIGHT,
    quickSearchRef,
  });

  return (
    <HeaderBase>
      <Container>
        <Link to="/" aria-label={translate(t => t.header.toStartPage)}>
          <SiteLogo />
        </Link>
        <Icons>
          <MyPagesButton />
          {isSvenssons ? <WishListButton /> : <NNWishListButton />}
          <CartButton />
          <MenuButton />
        </Icons>
      </Container>
      <QuickSearchWrapper
        css={styled(showQuickSearch && showQuickSearchDecorator, scrollPosition !== 0 && transitionDecorator)}
      >
        <QuickSearch ref={quickSearchRef} suggestionsEnabled={showQuickSearch} isLargeViewport={false} />
      </QuickSearchWrapper>
    </HeaderBase>
  );
}

const showQuickSearchDecorator = styled({ transform: 'translateY(0)', pointerEvents: 'auto' });

const QuickSearchWrapper = styled.div({
  transform: 'translateY(-100%)',
  pointerEvents: 'none',
  top: HEADER_HEIGHT,
  width: '100%',
  backgroundColor: t => t.header?.backgroundColor,
});

const transitionDecorator = styled(
  createTransitionDecorator({
    property: 'transform',
    duration: 200,
    timingFunction: 'ease-in-out',
  }),
);

function CheckoutHeader(props: { heading: string }) {
  const [startPageUrl] = useDynamicData<NordicNest.Api.WellknownPagesApiModel, string>({
    selector(data) {
      return data?.startPageUrl ?? '#';
    },
  });

  return (
    <CheckoutBase>
      <ButtonLink as={Theme.Transparent} to={startPageUrl} square>
        <BackIcon />
      </ButtonLink>
      <Heading>{props.heading}</Heading>
      {isSvenssons ? <WishListButton /> : <NNWishListButton />}
    </CheckoutBase>
  );
}

const Base = styled.header({
  display: 'flex',
  flexDirection: 'column',
  position: 'sticky',
  top: 0,
  alignItems: 'center',
  zIndex: ZIndex.Header,
});

const HeaderBase = styled(Base, {
  pointerEvents: 'none',
});

const CheckoutBase = styled(Base, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: HEADER_HEIGHT,
  backgroundColor: t => t.header?.backgroundColor,
});

const Container = styled.div({
  height: HEADER_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  zIndex: 1,
  justifyContent: 'space-between',
  width: '100%',
  padding: { left: t => t.sharedSpacing.m, right: t => t.sharedSpacing.xxs },
  backgroundColor: t => t.header?.backgroundColor,
  pointerEvents: 'auto',
});

const Icons = styled.div({
  display: 'flex',
  alignItems: 'center',
});

const Heading = styled.h1({
  fontSize: t => t.sharedFontSize.l,
  margin: {
    bottom: 0,
  },
});
